// AI generated
import type { Node, NodePositionChange, XYPosition } from '@xyflow/react'
import { HELPER_LINE_DISTANCE } from '../constants'
import { getNodeSize, toAbsolutePosition } from './bounds'

type GetHelperLinesResult = {
  horizontal?: number
  vertical?: number
  snapPosition: Partial<XYPosition>
}

type Bound = 'left' | 'right' | 'top' | 'bottom'
type Bounds = Record<Bound, number> & { width: number; height: number }

type Comparison = {
  /** the axis whose running distance and snap coordinate this entry narrows */
  axis: 'x' | 'y'
  /** edge of the dragged node A */
  a: Bound
  /** edge of the candidate node B that A is compared against */
  b: Bound
}

/**
 * The eight snap directions, in the order they are evaluated. Order is
 * behaviour: every entry narrows the running distance of its axis and compares
 * with a strict `<`, so an earlier entry wins a tie against a later one.
 */
const COMPARISONS = [
  // A stacked above B, left edges aligned
  { axis: 'x', a: 'left', b: 'left' },
  // A stacked above B, right edges aligned
  { axis: 'x', a: 'right', b: 'right' },
  // A to the right of B, A's left edge on B's right edge
  { axis: 'x', a: 'left', b: 'right' },
  // A to the left of B, A's right edge on B's left edge
  { axis: 'x', a: 'right', b: 'left' },
  // A beside B, top edges aligned
  { axis: 'y', a: 'top', b: 'top' },
  // A above B, A's bottom edge on B's top edge
  { axis: 'y', a: 'bottom', b: 'top' },
  // A beside B, bottom edges aligned
  { axis: 'y', a: 'bottom', b: 'bottom' },
  // A below B, A's top edge on B's bottom edge
  { axis: 'y', a: 'top', b: 'bottom' },
] as const satisfies readonly Comparison[]

/** A snap on the x axis draws a vertical guide, and vice versa. */
const GUIDE = { x: 'vertical', y: 'horizontal' } as const
const SIZE = { x: 'width', y: 'height' } as const

const boundsOf = (position: XYPosition, node: Node): Bounds => {
  const { width, height } = getNodeSize(node)

  return {
    left: position.x,
    right: position.x + width,
    top: position.y,
    bottom: position.y + height,
    width,
    height,
  }
}

/**
 * Every node is compared in canvas coordinates, because a node inside a group
 * carries a position relative to that group and would otherwise be measured
 * against a different origin than the nodes it is dragged past.
 *
 * The two results live in different spaces on purpose: `horizontal`/`vertical`
 * are the canvas coordinates `HelperLines` paints the guides at, while
 * `snapPosition` goes back into `change.position` and is therefore relative to
 * the dragged node's parent.
 */
export function getHelperLines(
  change: NodePositionChange,
  nodes: Node[],
  distance = HELPER_LINE_DISTANCE,
): GetHelperLinesResult {
  const result: GetHelperLinesResult = {
    horizontal: undefined,
    vertical: undefined,
    snapPosition: { x: undefined, y: undefined },
  }
  const nodeA = nodes.find((node) => node.id === change.id)

  if (!nodeA || !change.position) {
    return result
  }

  const byId = new Map(nodes.map((node) => [node.id, node]))
  const findNode = (id: string) => byId.get(id)

  const originA = toAbsolutePosition({ x: 0, y: 0 }, nodeA.parentId, findNode)
  const boundsA = boundsOf(
    toAbsolutePosition(change.position, nodeA.parentId, findNode),
    nodeA,
  )
  const closest = { x: distance, y: distance }

  for (const nodeB of nodes) {
    if (nodeB.id === nodeA.id) continue

    const boundsB = boundsOf(
      toAbsolutePosition(nodeB.position, nodeB.parentId, findNode),
      nodeB,
    )

    for (const { axis, a, b } of COMPARISONS) {
      const gap = Math.abs(boundsA[a] - boundsB[b])

      if (gap >= closest[axis]) continue

      // Snapping A's far edge onto the guide moves A's position by its own size
      const offset = a === 'right' || a === 'bottom' ? boundsA[SIZE[axis]] : 0

      result.snapPosition[axis] = boundsB[b] - offset - originA[axis]
      result[GUIDE[axis]] = boundsB[b]
      closest[axis] = gap
    }
  }

  return result
}
